import React, { useEffect, useState } from "react";
import { Microphone } from "../../assets/Icons/Microphone";
import { SendIcon } from "../../assets/Icons/SendIcon";
import { AILogo } from "../../assets/Icons/SparksSolid";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import "./OrderAssistantPanel.scss";
import NavArrowUp from "../../assets/Icons/NavArrowUp";
import FlowTick from "../../assets/Icons/FlowTick";
import GrayDot from "../../assets/Icons/GrayDot";
import { Chatbot } from "../Home/Chatbot";
// import ResponsiveFlowTick from "../../assets/Icons/ResponsiveFlowTick";
import { ORDER_DETAILS, ARIA_LABELS } from "../../util/constants";
import FlowCircle from "../../assets/Icons/FlowCircle";
import WarningTriangle from "../../assets/Icons/WarningTriangle";
import { WORKFLOW_STATUS_CONFIG_DATA } from "./orderAssistantPanelData";

const DATA_RECEIVED_STEPS = [
  "File Validation",
  "Field Mapping",
  "Data Validation",
];

const DATA_RECEIVED_FLOW_STATE = {
  IDLE: "idle",
  DONE: "done",
  IN_PROGRESS: "in-progress",
  FAILED: "failed",
};

const WORKFLOW_STATUS_CONFIG = WORKFLOW_STATUS_CONFIG_DATA;

const buildDataReceivedWorkflowConfig = (flowState = {}) => {
  const fileValidationStatus =
    flowState.fileValidationStatus || DATA_RECEIVED_FLOW_STATE.IDLE;
  const fileMappingStatus =
    flowState.fileMappingStatus || DATA_RECEIVED_FLOW_STATE.IDLE;
  const dataValidationStatus =
    flowState.dataValidationStatus || DATA_RECEIVED_FLOW_STATE.IDLE;

  const iconMap = {
    [DATA_RECEIVED_FLOW_STATE.DONE]: "TickIcon",
    [DATA_RECEIVED_FLOW_STATE.IN_PROGRESS]: "InProgressIcon",
    [DATA_RECEIVED_FLOW_STATE.FAILED]: "FailedIcon",
    [DATA_RECEIVED_FLOW_STATE.IDLE]: "InactiveIcon",
  };

  return {
    workflowHeader: "Data Received",
    workflowSteps: DATA_RECEIVED_STEPS,
    workFlowIcons: [
      iconMap[fileValidationStatus] || "InactiveIcon",
      iconMap[fileMappingStatus] || "InactiveIcon",
      iconMap[dataValidationStatus] || "InactiveIcon",
    ],
    stepper: 1,
  };
};

const normalizeStatusKey = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const normalizeRegionKey = (value) =>
  (value || "").toString().trim().toUpperCase();

const COMPLETENESS_L1_REVIEW_STATUS = normalizeStatusKey(
  "Completeness L1 Review",
);
const AWAITING_COMPLETENESS_CONFIRMATION_STATUS = normalizeStatusKey(
  "Awaiting Completeness confirmation",
);
const EMEIA_PREPARATION_IN_PROGRESS_STEPS = [
  "Preparation & completeness in-progress",
  "Completeness L1 Review",
  "Completeness Ready for Delivery",
];
const EMEIA_COMPLETENESS_L1_REVIEW_STEPS = [
  "Completeness L1 Review",
  "Completeness Ready for Delivery",
  "Awaiting Completeness confirmation",
];
const EMEIA_AWAITING_COMPLETENESS_STEPS = [
  "Awaiting Completeness confirmation",
  "Analyser & Memo in Progress",
  "Analyzer & Memo - L1 Review",
];
const EMEIA_PREPARATION_IN_PROGRESS_STATUSES = new Set([
  normalizeStatusKey("Preparation & completeness in-progress"),
  normalizeStatusKey("Preparation & completeness in- progress"),
]);

const getOrderRegion = (orderDetails) => {
  const mainDetailsSection = Array.isArray(orderDetails)
    ? orderDetails.find((section) => section?.section === "Main Details")
    : null;
  const regionField = mainDetailsSection?.pagefields?.find((field) => {
    const normalizedField = String(field?.field || "").toLowerCase();
    const normalizedLabel = String(field?.labelToShowOnUI || "").toLowerCase();

    return normalizedField === "regionid" || normalizedLabel === "region";
  });

  return (
    regionField?.fieldValue ||
    (Array.isArray(orderDetails)
      ? orderDetails[0]?.headerSection?.[0]?.region
      : orderDetails?.headerSection?.[0]?.region) ||
    null
  );
};

const resolveConditionalStatusKey = (normalizedStatus) => {
  // Detect combined US/EMEIA Completeness L2 status label
  if (
    normalizedStatus.includes("completenessl2review") &&
    normalizedStatus.includes("completenessreadyfordelivery")
  ) {
    // EMEIA path reaches Ready for Delivery at this stage; US reaches L2 Review
    return normalizedStatus.includes("emeia")
      ? "Completeness Ready for Delivery"
      : "Completeness L2 Review";
  }

  // Detect EMEIA-specific Ready for Delivery label
  if (
    normalizedStatus.includes("readyfordelivery") &&
    normalizedStatus.includes("emeia")
  ) {
    return "Analyser & Memo - Ready for Delivery";
  }

  // Detect combined US/EMEIA Delivered status label
  if (
    normalizedStatus.includes("deliveredforonshorereview") ||
    normalizedStatus.includes("deliveredanalyseremeia") ||
    normalizedStatus.includes("deliveredanalyzeremeia")
  ) {
    // EMEIA path → Delivered Analyser; US path → Delivered for Onshore Review
    return normalizedStatus.includes("emeia")
      ? "Delivered Analyzer"
      : "Delivered for Onshore Review";
  }

  return null;
};

const isDataReceivedWorkflowStatus = (normalizedStatus) => {
  if (
    !normalizedStatus ||
    normalizedStatus.includes("datareceivedincomplete")
  ) {
    return false;
  }

  return (
    normalizedStatus.includes("datareceived") ||
    normalizedStatus.includes("filevalidation") ||
    normalizedStatus.includes("filemapping") ||
    normalizedStatus.includes("triagemappingconfirmationpending") ||
    normalizedStatus.includes("datavalidation")
  );
};

const deriveDataReceivedFlowState = (
  normalizedStatus,
  flowState = {},
  dataTriageSubStage = null,
) => {
  const baseState = {
    fileValidationStatus:
      flowState.fileValidationStatus || DATA_RECEIVED_FLOW_STATE.IDLE,
    fileMappingStatus:
      flowState.fileMappingStatus || DATA_RECEIVED_FLOW_STATE.IDLE,
    dataValidationStatus:
      flowState.dataValidationStatus || DATA_RECEIVED_FLOW_STATE.IDLE,
  };

  if (!normalizedStatus) {
    return baseState;
  }

  // Handle "Data Received" status with different dataTriageSubStage values
  if (normalizedStatus === normalizeStatusKey("Data Received")) {
    const normalizedSubStage = dataTriageSubStage
      ? normalizeStatusKey(dataTriageSubStage)
      : null;

    // When dataTriageSubStage is "Data Validation" - all three steps are done
    if (normalizedSubStage === normalizeStatusKey("Data Validation")) {
      return {
        fileValidationStatus: DATA_RECEIVED_FLOW_STATE.DONE,
        fileMappingStatus: DATA_RECEIVED_FLOW_STATE.DONE,
        dataValidationStatus: DATA_RECEIVED_FLOW_STATE.DONE,
      };
    }

    // When dataTriageSubStage is "Field Validation" - File Validation & Field Mapping done, Data Validation in-progress
    if (normalizedSubStage === normalizeStatusKey("Field Validation")) {
      return {
        fileValidationStatus: DATA_RECEIVED_FLOW_STATE.DONE,
        fileMappingStatus: DATA_RECEIVED_FLOW_STATE.DONE,
        dataValidationStatus: DATA_RECEIVED_FLOW_STATE.IN_PROGRESS,
      };
    }

    // When dataTriageSubStage is "File Validation" - File Validation done, Field Mapping in-progress
    if (normalizedSubStage === normalizeStatusKey("File Validation")) {
      return {
        fileValidationStatus: DATA_RECEIVED_FLOW_STATE.DONE,
        fileMappingStatus: DATA_RECEIVED_FLOW_STATE.IN_PROGRESS,
        dataValidationStatus: DATA_RECEIVED_FLOW_STATE.IDLE,
      };
    }

    // When dataTriageSubStage is null/blank/undefined - File Validation in-progress
    return {
      fileValidationStatus: DATA_RECEIVED_FLOW_STATE.IN_PROGRESS,
      fileMappingStatus: DATA_RECEIVED_FLOW_STATE.IDLE,
      dataValidationStatus: DATA_RECEIVED_FLOW_STATE.IDLE,
    };
  }

  if (normalizedStatus.includes("failed")) {
    return {
      fileValidationStatus: DATA_RECEIVED_FLOW_STATE.DONE,
      fileMappingStatus: DATA_RECEIVED_FLOW_STATE.DONE,
      dataValidationStatus: DATA_RECEIVED_FLOW_STATE.FAILED,
    };
  }

  if (normalizedStatus.includes("datavalidation")) {
    return {
      fileValidationStatus: DATA_RECEIVED_FLOW_STATE.DONE,
      fileMappingStatus: DATA_RECEIVED_FLOW_STATE.DONE,
      dataValidationStatus: DATA_RECEIVED_FLOW_STATE.IN_PROGRESS,
    };
  }

  if (
    normalizedStatus.includes("triagemappingconfirmationpending") ||
    normalizedStatus.includes("filemapping")
  ) {
    return {
      fileValidationStatus: DATA_RECEIVED_FLOW_STATE.DONE,
      fileMappingStatus: DATA_RECEIVED_FLOW_STATE.IN_PROGRESS,
      dataValidationStatus: DATA_RECEIVED_FLOW_STATE.IDLE,
    };
  }

  if (normalizedStatus.includes("filevalidation")) {
    return {
      fileValidationStatus: DATA_RECEIVED_FLOW_STATE.IN_PROGRESS,
      fileMappingStatus: DATA_RECEIVED_FLOW_STATE.IDLE,
      dataValidationStatus: DATA_RECEIVED_FLOW_STATE.IDLE,
    };
  }

  return baseState;
};

const applyRegionalWorkflowOverrides = (
  workflowConfig,
  normalizedStatus,
  normalizedRegion,
) => {
  if (!workflowConfig) {
    return null;
  }

  if (normalizedRegion === "EMEIA") {
    if (EMEIA_PREPARATION_IN_PROGRESS_STATUSES.has(normalizedStatus)) {
      return {
        ...workflowConfig,
        workflowSteps: EMEIA_PREPARATION_IN_PROGRESS_STEPS,
      };
    }

    if (normalizedStatus === COMPLETENESS_L1_REVIEW_STATUS) {
      return {
        ...workflowConfig,
        workflowSteps: EMEIA_COMPLETENESS_L1_REVIEW_STEPS,
      };
    }

    if (normalizedStatus === AWAITING_COMPLETENESS_CONFIRMATION_STATUS) {
      return {
        ...workflowConfig,
        workflowSteps: EMEIA_AWAITING_COMPLETENESS_STEPS,
        workFlowIcons: ["InProgressIcon", "InactiveIcon", "InactiveIcon"],
        // workFlowIcons: ["TickIcon", "InProgressIcon", "InactiveIcon"],
      };
    }
  }

  if (
    normalizedRegion === "US" &&
    normalizedStatus === AWAITING_COMPLETENESS_CONFIRMATION_STATUS
  ) {
    return {
      ...workflowConfig,
      workflowSteps: [
        "Completeness L1 Review",
        "Completeness L2 Review",
        "Awaiting Completeness confirmation",
      ],
      workFlowIcons: ["TickIcon", "TickIcon", "InProgressIcon"],
    };
  }

  return workflowConfig;
};

// Helper function to get workflow config by status
export const getWorkflowConfigByStatus = (
  status,
  dataReceivedFlowState,
  region,
  dataTriageSubStage = null,
) => {
  if (!status) return null;

  const normalizedStatus = normalizeStatusKey(status);
  const normalizedRegion = normalizeRegionKey(region);

  if (
    dataReceivedFlowState?.dataValidationStatus ===
    DATA_RECEIVED_FLOW_STATE.DONE
  ) {
    return WORKFLOW_STATUS_CONFIG["Data Preparation and Completeness"];
  }

  if (isDataReceivedWorkflowStatus(normalizedStatus)) {
    return buildDataReceivedWorkflowConfig(
      deriveDataReceivedFlowState(
        normalizedStatus,
        dataReceivedFlowState,
        dataTriageSubStage,
      ),
    );
  }

  if (WORKFLOW_STATUS_CONFIG[status]) {
    return applyRegionalWorkflowOverrides(
      WORKFLOW_STATUS_CONFIG[status],
      normalizedStatus,
      normalizedRegion,
    );
  }

  const conditionalKey = resolveConditionalStatusKey(normalizedStatus);

  if (conditionalKey) {
    return WORKFLOW_STATUS_CONFIG[conditionalKey] || null;
  }

  const matchedKey = Object.keys(WORKFLOW_STATUS_CONFIG).find(
    (key) => normalizeStatusKey(key) === normalizedStatus,
  );

  return matchedKey
    ? applyRegionalWorkflowOverrides(
        WORKFLOW_STATUS_CONFIG[matchedKey],
        normalizedStatus,
        normalizedRegion,
      )
    : null;
};

// Collapsible section displaying workflow status with progress indicators
function CollapsibleStatus({
  orderDetails,
  isOpen,
  setIsOpen,
  isTall,
  status,
  currentStage,
  dataReceivedFlowState,
  region,
  dataTriageSubStage,
  orderActivityType,
}) {
  // Returns workflow configuration matching the current status
  const getWorkflowConfig = () => {
    return getWorkflowConfigByStatus(
      status,
      dataReceivedFlowState,
      region,
      dataTriageSubStage,
    );
  };

  // Renders the appropriate icon based on workflow state
  const renderIcon = (iconType) => {
    switch (iconType) {
      case "TickIcon":
        return <FlowTick />;
      case "InProgressIcon":
        return <FlowCircle />;
      case "FailedIcon":
        return <WarningTriangle width={16} height={16} color="#b26100" />;
      case "InactiveIcon":
      default:
        return <GrayDot />;
    }
  };

  // Returns CSS class based on icon type and position
  const getFlowItemClass = (iconType, isLast) => {
    let className = "order-panel__flow-item";

    switch (iconType) {
      case "TickIcon":
        className += " is-done";
        break;
      case "InProgressIcon":
        className += " is-pending";
        break;
      case "FailedIcon":
        className += " is-failed";
        break;
      case "InactiveIcon":
      default:
        className += " is-idle";
        break;
    }

    if (isLast) {
      className += " is-last";
    }

    return className;
  };

  const workflowConfig = getWorkflowConfig();

  // Check if orderActivityType is not "Active" - if so, use it as header and disable collapsible
  const isNotActiveOrder =
    orderActivityType &&
    normalizeStatusKey(orderActivityType) !== normalizeStatusKey("Active");

  const workflowHeader = isNotActiveOrder
    ? orderActivityType
    : status || workflowConfig?.workflowHeader || ORDER_DETAILS.CURRENT_STATUS;

  // Check if status is "Project Complete" - if so, disable collapsible functionality
  const isProjectComplete =
    normalizeStatusKey(status) === normalizeStatusKey("Project Complete");

  // Disable collapsible for Project Complete OR non-Active orders
  const isCollapsibleDisabled = isProjectComplete || isNotActiveOrder;

  // Force closed state when collapsible is disabled
  const effectiveIsOpen = isCollapsibleDisabled ? false : isOpen;

  return (
    <div
      className={`order-panel__collapsible ${isTall ? "is-tall" : ""}`}
      style={{
        height: effectiveIsOpen ? (isTall ? "auto" : "156px") : "36px",
        minHeight: !effectiveIsOpen ? "36px" : undefined,
      }}
    >
      <div className={`order-panel__collapsible-header `}>
        <div className="order-panel__section-title">{workflowHeader}</div>
        {!isCollapsibleDisabled && (
          <div
            className="order-panel__icon-btn"
            aria-label={
              effectiveIsOpen ? ARIA_LABELS.COLLAPSE : ARIA_LABELS.EXPAND
            }
            onClick={() => setIsOpen(!isOpen)}
          >
            {effectiveIsOpen ? (
              <NavArrowUp />
            ) : (
              <NavDownArrow width={"16"} height={"16"} />
            )}
          </div>
        )}
      </div>
      {effectiveIsOpen && (
        <div className="order-panel__collapsible-body">
          <div className="order-panel__flow">
            {workflowConfig ? (
              // Dynamic workflow rendering based on status
              workflowConfig.workflowSteps.map((step, index) => {
                const iconType = workflowConfig.workFlowIcons[index];
                const isLast =
                  index === workflowConfig.workflowSteps.length - 1;

                return (
                  <div
                    key={index}
                    className={getFlowItemClass(iconType, isLast)}
                  >
                    <div className="order-panel__rail">
                      <span className="order-panel__flow-dot">
                        {renderIcon(iconType)}
                      </span>
                      {!isLast && (
                        <span className="order-panel__flow-connector" />
                      )}
                    </div>
                    <span className="order-panel__flow-label">{step}</span>
                  </div>
                );
              })
            ) : (
              // Default workflow when no status match found
              <>
                <div className="order-panel__flow-item is-idle is-last">
                  <div className="order-panel__rail">
                    <span className="order-panel__flow-dot">
                      <GrayDot />
                    </span>
                    <span className="order-panel__flow-connector" />
                  </div>
                  <span className="order-panel__flow-label">
                    {ORDER_DETAILS.NO_STATUS}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Main panel component with collapsible status and chatbot
export default function OrderAssistantPanel({
  orderDetails,
  onActionClick,
  onRefreshOrderDetails,
  // dataReceivedFlowState,
  dataReceivedFlowState,
  attachFilesMessages,
  incompleteDataMessage,
  isActiveOrder = true,
  onAILoadingChange,
  trailRefreshKey,
  notificationNavigationId,
}) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!orderDetails) {
      return;
    }

    const headerSection = Array.isArray(orderDetails)
      ? orderDetails[0]?.headerSection?.[0]
      : orderDetails?.headerSection?.[0];
    const region = getOrderRegion(orderDetails);

    console.log("Order details API values:", {
      currentSubStage: headerSection?.currentSubStage || null,
      currentStage:
        headerSection?.currentStage ||
        (Array.isArray(orderDetails)
          ? orderDetails[0]?.status
          : orderDetails?.status) ||
        null,
      region,
    });
  }, [orderDetails]);

  const isTall = orderDetails?.status === "Data Triage";

  const status =
    orderDetails?.length > 0 &&
    orderDetails[0].headerSection[0].currentSubStage;
  const currentStage =
    orderDetails?.length > 0 && orderDetails[0].headerSection[0].currentStage;
  const orderId =
    orderDetails?.length > 0 && orderDetails[0].headerSection[0].axonId;
  const dataTriageSubStage =
    orderDetails?.length > 0 &&
    orderDetails[0].headerSection[0].dataTriageSubStage;
  const region = getOrderRegion(orderDetails);
  const orderActivityType =
    orderDetails?.length > 0 &&
    orderDetails[0].headerSection[0].orderActivityType;

  // Get workflow steps based on status for dynamic action buttons
  const workflowConfig = getWorkflowConfigByStatus(
    status,
    dataReceivedFlowState,
    region,
    dataTriageSubStage,
  );
  const workflowSteps = workflowConfig?.workflowSteps || [];

  // Don't render if orderDetails is not available yet
  if (
    !orderDetails ||
    (Array.isArray(orderDetails) && orderDetails.length === 0)
  ) {
    return null;
  }

  return (
    <>
      <CollapsibleStatus
        orderDetails={orderDetails}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isTall={isTall}
        status={status}
        currentStage={currentStage}
        dataReceivedFlowState={dataReceivedFlowState}
        region={region}
        dataTriageSubStage={dataTriageSubStage}
        orderActivityType={orderActivityType}
      />
      <div className="order-panel">
        <Chatbot
          variant="order-details"
          hasStatusHeader
          orderDetails={orderDetails}
          orderId={orderId || orderDetails?.orderId}
          isOpen={isOpen}
          onActionClick={onActionClick}
          workflowSteps={workflowSteps}
          onRefreshOrderDetails={onRefreshOrderDetails}
          attachFilesMessages={attachFilesMessages}
          incompleteDataMessage={incompleteDataMessage}
          isDisabled={!isActiveOrder}
          onAILoadingChange={onAILoadingChange}
          trailRefreshKey={trailRefreshKey}
          notificationNavigationId={notificationNavigationId}
        />
      </div>
    </>
  );
}
